import React from 'react';
import { Button, DialogActions, DialogContent, Grid, Popover, Paper } from '@mui/material';

import Form from '../Form';
import ColorPickerField from '../fields/ColorPickerField';
import NumberTextField from '../fields/NumberTextField';
import useSettings from '../hooks/useSettings';
import { resizeImageWithColor } from './ImageUtils';

interface ImageEditorResizeDialogProps {
	anchorElement?: HTMLButtonElement;
	open: boolean;
	handleClose: () => void;
	name: string;
	image: string | null;
	setImage: React.Dispatch<React.SetStateAction<string | null>>;
	imageDimensions?: {
		width: number;
		height: number;
	};
}

export default function ImageEditorResizeDialog({
	anchorElement,
	open,
	handleClose,
	name,
	image,
	setImage,
	imageDimensions
}: ImageEditorResizeDialogProps) {
	const { t } = useSettings();

	const [properties, setProperties] = React.useState({
		width: imageDimensions?.width,
		height: imageDimensions?.height,
		hexColor: '#ffffff'
	});

	const sendChange = (n, v) => setProperties(s => ({ ...s, [n]: v }));

	const resizeImage = async () => {
		if (image !== null && !!properties.width && !!properties.height) {
			const resizedImage = await resizeImageWithColor(
				image,
				properties.width,
				properties.height,
				properties.hexColor,
				name
			);

			if (resizedImage !== null) {
				const file = new File([resizedImage], name, { type: resizedImage.type });

				const reader = new FileReader();
				reader.addEventListener('load', () => setImage(reader.result as string));
				reader.readAsDataURL(file);
			}
		}

		handleClose();
	};

	return (
		<Popover
			anchorEl={anchorElement}
			open={open}
			onClose={handleClose}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			transformOrigin={{ vertical: 'top', horizontal: 'left' }}
		>
			<Paper>
				<DialogContent>
					<Form>
						<Grid container spacing={2}>
							<Grid size={6}>
								<NumberTextField
									name="width"
									label={t('IMAGE_RESIZER_WIDTH')}
									value={properties.width}
									sendChange={sendChange}
									isEdit
								/>
							</Grid>
							<Grid size={6}>
								<NumberTextField
									name="height"
									label={t('IMAGE_RESIZER_HEIGHT')}
									value={properties.height}
									sendChange={sendChange}
									isEdit
								/>
							</Grid>
							<Grid size={12}>
								<ColorPickerField
									name="hexColor"
									sendChange={sendChange}
									value={properties.hexColor}
									isEdit
								/>
							</Grid>
						</Grid>
					</Form>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>{t('DIALOG_ACTION_CANCEL')}</Button>
					<Button onClick={resizeImage} disabled={!!properties.width && !!properties.height}>
						{t('DIALOG_ACTION_CONFIRM')}
					</Button>
				</DialogActions>
			</Paper>
		</Popover>
	);
}
